// Daily commitments board (Oct 2, 2026) — /canvas/manager-audit?mode=today
//
// Tue–Fri by 10:00 AM ET the TL/ASM confirms each active canvasable person's
// day: blocks, area, team knock in/out, and today's apps / claims / signed IA
// goals (pre-filled from the weekly goal). Tue–Thu open on the team-knock card.
// Writes are crew managers only (RLS); everyone else sees it read-only.
//
// Canvas areas (Oct 19, 2026): each person row has a 📐 dropdown showing the
// areas they're assigned to (with their numbers in each) where a crew manager
// can add or remove them, and every assigned area is listed under the people
// with its full stats. Area numbers are live, not tied to the day picker.

import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../context/AuthContext";
import { isCrewManager } from "../../crew/crewAuth";
import { fetchWeeklyGoalsMap, type WeeklyGoalsMap } from "../../crew/crewQueries";
import { addDaysISO, mondayOf } from "../../../lib/weeklySpiffs";
import {
  BLOCKS, CANVASABLE_ROLES, CONFIRM_DOWS, KNOCK_DOWS,
  type Blocks, type DailyCommitment, type DayActuals, type TeamKnock,
  cancelKnock, dayLabel, deleteCommitment, dowOf, fetchCommitments, fetchDayActuals,
  fetchKnockShowed, fetchKnocks, fmtClock, minutesTo10, prefillGoals, saveCommitment,
  saveKnock, sendKnockAlert, todayET,
  type DaySummaryRecord, fetchDaySummary, sendDaySummary,
} from "../../../lib/dailyCommitments";
import {
  areaName, fetchAreaOutlines, fetchAreaReview, fetchDayStops, useAreaStats, type AreaOutline, type AreaStats,
} from "../../../lib/canvasAreas";
import { areaIdsKey, rangeLabel, sortAreasForDay, weekRange, type DayStop, type ReviewArea } from "../../../lib/dailyAreas";
import PersonAreasPanel from "../areas/PersonAreasPanel";
import AreaBoard from "../areas/AreaBoard";
import PersonStops from "../areas/PersonStops";
import OpenPastAreas from "../areas/OpenPastAreas";
import DayAreasMap, { dayMapAreas, reviewMapAreas } from "../areas/DayAreasMap";

interface Person { id: string; full_name: string; role: string }

const ROLE_ORDER: Record<string, number> = { asm: 0, team_lead: 1, restore_manager: 2, canvasser: 3 };
const ROLE_SHORT: Record<string, string> = { asm: "ASM", team_lead: "TL", restore_manager: "RM", canvasser: "CNV" };
const BLOCK_LABEL: Record<string, string> = { am: "AM", pm: "PM", eve: "EVE" };

function fmt(n: number | null | undefined): string {
  if (n == null) return "—";
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}
function timeShort(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" }).replace(" ", "").toLowerCase();
}

export default function DailyBoard() {
  const { member } = useAuth();
  const canWrite = isCrewManager(member);
  // ?day=YYYY-MM-DD opens a specific day (links from texts / training decks).
  const [day, setDay] = useState<string>(() => {
    const q = new URLSearchParams(window.location.search).get("day");
    return q && /^\d{4}-\d{2}-\d{2}$/.test(q) ? q : todayET();
  });
  const [people, setPeople] = useState<Person[]>([]);
  const [rows, setRows] = useState<Record<string, DailyCommitment>>({});
  const [knocks, setKnocks] = useState<TeamKnock[]>([]);
  const [weekly, setWeekly] = useState<WeeklyGoalsMap>({});
  const [actuals, setActuals] = useState<Record<string, DayActuals>>({});
  const [showed, setShowed] = useState<Record<string, Set<string>>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [clock, setClock] = useState(0); // re-render the countdown each minute
  const [summary, setSummary] = useState<DaySummaryRecord | null>(null);
  // Every active canvas area (live; realtime inside the hook).
  const { areas, error: areasErr, reload: reloadAreas } = useAreaStats();

  const dow = dowOf(day);
  const isKnockDay = KNOCK_DOWS.has(dow);
  const isConfirmDay = CONFIRM_DOWS.has(dow);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const weekStart = mondayOf(new Date(day + "T12:00:00Z"));
      const [roster, commits, ks, wk] = await Promise.all([
        supabase.from("team_roster").select("id, full_name, role").eq("is_active", true).eq("on_leave", false).in("role", [...CANVASABLE_ROLES]),
        fetchCommitments(day),
        fetchKnocks(day),
        fetchWeeklyGoalsMap(weekStart),
      ]);
      if (roster.error) throw roster.error;
      const ppl = ((roster.data ?? []) as Person[]).sort((a, b) =>
        (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || a.full_name.localeCompare(b.full_name));
      setPeople(ppl);
      setRows(Object.fromEntries(commits.map((c) => [c.user_id, c])));
      setKnocks(ks);
      setWeekly(wk);
      const [act, sh] = await Promise.all([fetchDayActuals(day, ppl.map((p) => p.id)), fetchKnockShowed(day, ks)]);
      setActuals(act);
      setShowed(sh);
      setSummary(await fetchDaySummary(day));
    } catch (e) {
      setErr((e as Error).message ?? "Couldn't load the day.");
    } finally {
      setLoading(false);
    }
  }, [day]);

  useEffect(() => { setLoading(true); setEditing(null); void load(); }, [load, tick]);

  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    const bump = () => { if (t) clearTimeout(t); t = setTimeout(() => setTick((n) => n + 1), 800); };
    const ch = supabase.channel(`daily-board-${day}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "daily_commitments", filter: `day=eq.${day}` }, bump)
      .on("postgres_changes", { event: "*", schema: "public", table: "team_knocks", filter: `knock_date=eq.${day}` }, bump)
      .subscribe();
    return () => { if (t) clearTimeout(t); void supabase.removeChannel(ch); };
  }, [day]);

  useEffect(() => {
    const i = setInterval(() => setClock((n) => n + 1), 60_000);
    return () => clearInterval(i);
  }, []);

  // Daily areas (Oct 23, 2026): where each person has to be — builds, adjuster
  // meetings, reinspections — and the day's map (day or week).
  const [stops, setStops] = useState<DayStop[] | null>(null);
  const [outlines, setOutlines] = useState<Map<string, AreaOutline> | null>(null);
  const [mapView, setMapView] = useState<"day" | "week">("day");
  const [weekAreas, setWeekAreas] = useState<ReviewArea[] | null>(null);
  const [weekStops, setWeekStops] = useState<DayStop[] | null>(null);

  // A new day starts from nothing; a refresh (tick) keeps what's on screen
  // until the new rows arrive so the pins and stop chips don't blink.
  useEffect(() => { setStops(null); }, [day]);
  useEffect(() => {
    let cancelled = false;
    fetchDayStops(day).then((s) => { if (!cancelled) setStops(s); }).catch(() => { if (!cancelled) setStops((prev) => prev ?? []); });
    return () => { cancelled = true; };
  }, [day, tick]);

  // Outlines (and the week review) only change when an area is added or
  // removed — not on every live stats refresh — so key them on the area ids.
  const areaKey = areaIdsKey(areas);
  useEffect(() => {
    let cancelled = false;
    fetchAreaOutlines().then((m) => { if (!cancelled) setOutlines(m); }).catch(() => { /* the map just has no outlines */ });
    return () => { cancelled = true; };
  }, [areaKey]);

  useEffect(() => {
    if (mapView !== "week") return;
    let cancelled = false;
    const { from, to } = weekRange(day);
    Promise.all([fetchAreaReview(from, to), fetchDayStops(from, to).catch(() => [] as DayStop[])])
      .then(([a, s]) => { if (!cancelled) { setWeekAreas(a); setWeekStops(s); } })
      .catch(() => { if (!cancelled) { setWeekAreas((prev) => prev ?? []); setWeekStops((prev) => prev ?? []); } });
    return () => { cancelled = true; };
  }, [mapView, day, areaKey]);

  const mapAreas = useMemo(() => {
    if (mapView === "week") {
      const { from, to } = weekRange(day);
      return weekAreas ? reviewMapAreas(weekAreas, from, to) : [];
    }
    return areas && outlines ? dayMapAreas(areas, outlines, day) : [];
  }, [mapView, weekAreas, areas, outlines, day]);

  const counts = useMemo(() => {
    let confirmed = 0, off = 0, late = 0;
    for (const p of people) {
      const r = rows[p.id];
      if (!r) continue;
      if (r.is_off) off++; else confirmed++;
      if (r.confirmed_late) late++;
    }
    return { confirmed, off, late, open: people.length - confirmed - off };
  }, [people, rows]);

  void clock;
  const mins = minutesTo10(day);
  const isToday = day === todayET();

  return (
    <div className="space-y-4">
      {/* Day nav + status */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setDay(addDaysISO(day, -1))} className="rounded-md border border-slate-200 px-2 py-1 text-sm font-bold text-slate-600 hover:bg-slate-50" aria-label="Previous day">‹</button>
          <div className="min-w-[150px] text-center">
            <div className="text-base font-bold text-slate-900">{dayLabel(day, { weekday: "long", month: "short", day: "numeric" })}</div>
            <div className="text-[11px] text-slate-500">
              {isKnockDay ? "Team knock day · confirm by 10:00" : isConfirmDay ? "Confirm by 10:00" : "No 10am confirmation today"}
            </div>
          </div>
          <button onClick={() => setDay(addDaysISO(day, 1))} className="rounded-md border border-slate-200 px-2 py-1 text-sm font-bold text-slate-600 hover:bg-slate-50" aria-label="Next day">›</button>
          {!isToday && <button onClick={() => setDay(todayET())} className="rounded-md px-2 py-1 text-xs font-semibold text-orange-600 hover:bg-orange-50">Today</button>}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">{counts.confirmed} confirmed</span>
          {counts.off > 0 && <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-600">{counts.off} off</span>}
          <span className={`rounded-full px-2.5 py-1 font-semibold ${counts.open ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{counts.open} not yet</span>
          {counts.late > 0 && <span className="rounded-full bg-rose-50 px-2.5 py-1 font-semibold text-rose-700">{counts.late} late</span>}
          {isToday && isConfirmDay && counts.open > 0 && (
            <span className={`rounded-full px-2.5 py-1 font-bold ${mins > 0 ? "bg-slate-900 text-white" : "bg-rose-600 text-white"}`}>
              {mins > 0 ? `${mins} min to 10:00` : "10:00 passed"}
            </span>
          )}
        </div>
      </div>

      {err && <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{err} <button className="ml-2 font-semibold underline" onClick={() => setTick((n) => n + 1)}>Retry</button></div>}

      {canWrite && (
        <SubmitDayBar day={day} summary={summary} open={counts.open}
          onSent={() => setTick((n) => n + 1)} />
      )}

      <KnockPanel day={day} knocks={knocks} isKnockDay={isKnockDay} canWrite={canWrite} people={people}
        showed={showed} rows={rows} myId={member?.id ?? null} onChanged={() => setTick((n) => n + 1)} />

      {/* Daily areas from earlier days nobody has expired */}
      <OpenPastAreas areas={areas} day={day} canWrite={canWrite} onChanged={() => void reloadAreas()} />

      {/* People */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
          <div className="text-sm font-bold text-slate-900">Today's confirmations</div>
          <div className="text-[11px] text-slate-500">{canWrite ? "Tap a person to confirm their day" : "Read-only — your TL/ASM confirms"}</div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-sm text-slate-400">Loading…</div>
        ) : people.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">No active team members.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {people.map((p) => (
              <PersonRow key={p.id} person={p} row={rows[p.id]} knocks={knocks} actual={actuals[p.id]}
                weekly={weekly[p.id]} day={day} canWrite={canWrite} myId={member?.id ?? null}
                open={editing === p.id} onToggle={() => setEditing((e) => (e === p.id ? null : p.id))}
                onSaved={() => { setEditing(null); setTick((n) => n + 1); }}
                areas={areas} onAreasChanged={() => void reloadAreas()}
                stops={stops} people={people} />
            ))}
          </ul>
        )}
      </div>
      <p className="text-[11px] text-slate-500">
        Goals are pre-filled from each person's weekly goal (apps weighted to Tue–Thu) and can be changed on the call.
        "Done" counts the same activity as the Crew board. A confirmation after 10:00 is marked late.
        At 9:30 James and Alex get a text listing who's still open; at 10:00 Alex gets the late list. "Submit day" texts the full summary to the TLs, ASM, Jennifer, Chad and Greg.
        Each person's build or adjuster meeting for the day is listed under their name; "Area here" draws their area for the day around it.
      </p>

      {/* The day's map — today's areas in color, standing areas dashed, every stop pinned */}
      <DayAreasMap
        areas={mapAreas}
        stops={mapView === "week" ? (weekStops ?? []) : (stops ?? [])}
        title={mapView === "week"
          ? `This week's map · ${rangeLabel(weekRange(day), "week").replace("Week of ", "")}`
          : isToday ? "Today's map" : `Map for ${dayLabel(day)}`}
        subtitle={mapView === "week"
          ? "Daily areas colored by weekday, expired ones included · standing areas dashed · the week's builds and adjuster meetings pinned"
          : "Daily areas in color · standing areas dashed · every build, adjuster meeting and reinspection pinned"}
        empty={mapView === "week"
          ? (weekAreas ? "No areas this week yet." : "Loading the week…")
          : (areas ? `No areas drawn for ${isToday ? "today" : "this day"} yet${canWrite ? " — tap Area here on a person's stop" : ""}.` : "Loading…")}
        right={
          <>
            <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
              <button type="button" onClick={() => setMapView("day")}
                className={`rounded-md px-2.5 py-1 text-[11px] font-bold ${mapView === "day" ? "bg-orange-500 text-white" : "text-slate-600 hover:bg-slate-200"}`}>Day</button>
              <button type="button" onClick={() => setMapView("week")}
                className={`rounded-md px-2.5 py-1 text-[11px] font-bold ${mapView === "week" ? "bg-orange-500 text-white" : "text-slate-600 hover:bg-slate-200"}`}>Week</button>
            </div>
            <a href="/canvas?areas=on" target="_blank" rel="noopener" className="text-[11px] font-bold text-orange-700">Open on canvas ↗</a>
          </>
        }
      />

      <AreaBoard areas={areas} error={areasErr} people={people} canWrite={canWrite} day={day}
        onChanged={() => void reloadAreas()} onRetry={() => void reloadAreas()} />
    </div>
  );
}

// ── Submit day: texts the summary to TLs, ASMs, Jennifer, Chad, Greg ─────────

function SubmitDayBar({ day, summary, open, onSent }: {
  day: string; summary: DaySummaryRecord | null; open: number; onSent: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { setConfirming(false); setMsg(null); }, [day]);

  async function send() {
    setSending(true); setMsg(null);
    try {
      const r = await sendDaySummary(day);
      setMsg(`Summary texted to ${r.sent} of ${r.recipients}.`);
      setConfirming(false);
      onSent();
    } catch (e) {
      setMsg((e as Error).message);
    } finally { setSending(false); }
  }

  const needsConfirm = !!summary || open > 0;
  return (
    <div className={`rounded-xl border p-3 ${summary ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-white"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-bold text-slate-900">📤 Submit the day</div>
          <div className="text-[11px] text-slate-600">
            {summary
              ? `Submitted ${timeShort(summary.sent_at)}${summary.by_name ? ` by ${summary.by_name.split(" ")[0]}` : ""} · texted to the TLs, ASM, Jennifer, Chad and Greg`
              : "When everyone's confirmed, send the summary to the TLs, ASM, Jennifer, Chad and Greg."}
          </div>
        </div>
        {confirming ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-amber-700">
              {summary ? "Already sent — send the updated day again?" : `${open} not confirmed yet — submit anyway?`}
            </span>
            <button onClick={send} disabled={sending} className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">{sending ? "Sending…" : "Yes, send it"}</button>
            <button onClick={() => setConfirming(false)} className="rounded-md px-2 py-1.5 text-xs font-semibold text-slate-500">Cancel</button>
          </div>
        ) : (
          <button onClick={() => (needsConfirm ? setConfirming(true) : void send())} disabled={sending}
            className={`rounded-md px-3 py-1.5 text-xs font-bold disabled:opacity-50 ${summary ? "border border-emerald-300 bg-white text-emerald-700" : "bg-emerald-600 text-white"}`}>
            {sending ? "Sending…" : summary ? "Resend summary…" : "Submit day"}
          </button>
        )}
      </div>
      {msg && <div className="mt-2 rounded-md bg-white px-2 py-1.5 text-xs text-slate-700 ring-1 ring-slate-200">{msg}</div>}
    </div>
  );
}

// ── Team knocks ──────────────────────────────────────────────────────────────

function KnockPanel({ day, knocks, isKnockDay, canWrite, people, showed, rows, myId, onChanged }: {
  day: string; knocks: TeamKnock[]; isKnockDay: boolean; canWrite: boolean; people: Person[];
  showed: Record<string, Set<string>>; rows: Record<string, DailyCommitment>; myId: string | null; onChanged: () => void;
}) {
  const [formFor, setFormFor] = useState<TeamKnock | "new" | null>(null);
  const [sending, setSending] = useState(false);
  const [confirmResend, setConfirmResend] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const leaders = people.filter((p) => p.role !== "canvasser");
  const anySent = knocks.some((k) => k.alert_sent_at);
  const nameOf = (id: string | null) => people.find((p) => p.id === id)?.full_name ?? null;

  if (!isKnockDay && knocks.length === 0 && !canWrite) return null;

  async function send() {
    setSending(true); setMsg(null);
    try {
      const r = await sendKnockAlert(day);
      setMsg(`Team alert sent to ${r.sent} of ${r.recipients} people.`);
      setConfirmResend(false);
      onChanged();
    } catch (e) {
      setMsg((e as Error).message);
    } finally { setSending(false); }
  }

  return (
    <div className={`rounded-xl border p-3 ${isKnockDay ? "border-orange-200 bg-orange-50/40" : "border-slate-200 bg-white"}`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-bold text-slate-900">🚪 Team knock{knocks.length > 1 ? "s" : ""}</div>
          <div className="text-[11px] text-slate-500">
            {isKnockDay ? "Tue–Thu carve-out for app generation. Set it up first, then send the alert." : "Optional today."}
          </div>
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setFormFor("new")} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50">
              + {knocks.length ? "Add another knock" : "Set up the knock"}
            </button>
            {knocks.length > 0 && (anySent && !confirmResend ? (
              <button onClick={() => setConfirmResend(true)} className="rounded-md border border-orange-300 bg-white px-3 py-1.5 text-xs font-bold text-orange-700">Resend alert…</button>
            ) : (
              <button onClick={send} disabled={sending} className="rounded-md bg-orange-500 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">
                {sending ? "Sending…" : confirmResend ? "Yes, text everyone again" : "Send team alert"}
              </button>
            ))}
          </div>
        )}
      </div>
      {msg && <div className="mb-2 rounded-md bg-white px-2 py-1.5 text-xs text-slate-700 ring-1 ring-slate-200">{msg}</div>}

      {knocks.length === 0 && formFor !== "new" && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-3 text-xs text-slate-500">
          {isKnockDay ? "No knock set up yet for today." : "No knock today."}
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        {knocks.map((k) => {
          const inCount = Object.values(rows).filter((r) => r.team_knock_id === k.id && r.knock_status === "in").length;
          const showedCount = showed[k.id]?.size ?? 0;
          return formFor !== "new" && typeof formFor === "object" && formFor?.id === k.id ? null : (
            <div key={k.id} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-bold text-slate-900">Knock {k.label} · {fmtClock(k.starts_at)} – {fmtClock(k.ends_at)}</div>
                {canWrite && (
                  <div className="flex gap-1">
                    <button onClick={() => setFormFor(k)} className="rounded px-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100">Edit</button>
                    <button onClick={async () => { await cancelKnock(k.id).catch((e) => setMsg((e as Error).message)); onChanged(); }} className="rounded px-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50">Cancel</button>
                  </div>
                )}
              </div>
              <div className="mt-1 text-xs text-slate-600">📍 {k.meet_location}</div>
              {k.area_label && <div className="text-xs text-slate-600">Area: {k.area_label}</div>}
              {k.notes && <div className="text-xs text-slate-500">{k.notes}</div>}
              <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                {k.lead_id && <span className="rounded-full bg-orange-100 px-2 py-0.5 font-semibold text-orange-700">Lead: {nameOf(k.lead_id)}</span>}
                <span className={`rounded-full px-2 py-0.5 font-semibold ${k.alert_sent_at ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {k.alert_sent_at ? `Alert sent ${timeShort(k.alert_sent_at)}` : "Alert not sent"}
                </span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600">{inCount} in</span>
                {showedCount > 0 && <span className="rounded-full bg-sky-50 px-2 py-0.5 font-semibold text-sky-700">{showedCount} on shift during it</span>}
              </div>
            </div>
          );
        })}
        {formFor && (
          <KnockForm day={day} knock={formFor === "new" ? null : formFor} existing={knocks} leaders={leaders} myId={myId}
            onDone={() => { setFormFor(null); onChanged(); }} onCancel={() => setFormFor(null)} />
        )}
      </div>
    </div>
  );
}

function KnockForm({ day, knock, existing, leaders, myId, onDone, onCancel }: {
  day: string; knock: TeamKnock | null; existing: TeamKnock[]; leaders: Person[]; myId: string | null;
  onDone: () => void; onCancel: () => void;
}) {
  const nextLabel = String.fromCharCode(65 + existing.length);
  const base = knock ?? existing[0] ?? null;
  const [label, setLabel] = useState(knock?.label ?? nextLabel);
  const [start, setStart] = useState((base?.starts_at ?? "16:30").slice(0, 5));
  const [end, setEnd] = useState((base?.ends_at ?? "19:00").slice(0, 5));
  const [meet, setMeet] = useState(knock?.meet_location ?? "");
  const [area, setArea] = useState(knock?.area_label ?? "");
  const [notes, setNotes] = useState(knock?.notes ?? "");
  const [lead, setLead] = useState<string>(knock?.lead_id ?? myId ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!meet.trim()) { setErr("Add the meeting spot."); return; }
    if (end <= start) { setErr("The end time has to be after the start."); return; }
    if (!myId) { setErr("Your roster record didn't load — refresh and try again."); return; }
    setSaving(true); setErr(null);
    try {
      await saveKnock({ id: knock?.id, knock_date: day, label: label.trim() || nextLabel, starts_at: start, ends_at: end,
        meet_location: meet.trim(), area_label: area.trim() || null, notes: notes.trim() || null, lead_id: lead || null }, myId);
      onDone();
    } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  }

  const input = "w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm";
  return (
    <div className="rounded-lg border-2 border-orange-300 bg-white p-3 sm:col-span-2">
      <div className="grid gap-2 sm:grid-cols-4">
        <label className="text-[11px] font-semibold text-slate-500">Knock
          <input id="knock-label" className={input} value={label} onChange={(e) => setLabel(e.target.value.slice(0, 3))} />
        </label>
        <label className="text-[11px] font-semibold text-slate-500">Starts
          <input id="knock-start" type="time" className={input} value={start} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label className="text-[11px] font-semibold text-slate-500">Ends
          <input id="knock-end" type="time" className={input} value={end} onChange={(e) => setEnd(e.target.value)} />
        </label>
        <label className="text-[11px] font-semibold text-slate-500">Led by
          <select id="knock-lead" className={input} value={lead} onChange={(e) => setLead(e.target.value)}>
            <option value="">—</option>
            {leaders.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
          </select>
        </label>
        <label className="text-[11px] font-semibold text-slate-500 sm:col-span-2">Meeting spot
          <input id="knock-meet" className={input} placeholder="Kroger lot, 3550 Old Milton Pkwy, Alpharetta" value={meet} onChange={(e) => setMeet(e.target.value)} />
        </label>
        <label className="text-[11px] font-semibold text-slate-500 sm:col-span-2">Area
          <input id="knock-area" className={input} placeholder="Windward / Hwy 9 · Jul 29 storm, 1.75″" value={area} onChange={(e) => setArea(e.target.value)} />
        </label>
        <label className="text-[11px] font-semibold text-slate-500 sm:col-span-4">Notes (in the text)
          <input id="knock-notes" className={input} placeholder="Bring door hangers" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>
      {err && <div className="mt-2 text-xs text-rose-600">{err}</div>}
      <div className="mt-2 flex gap-2">
        <button onClick={save} disabled={saving} className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">{saving ? "Saving…" : knock ? "Save knock" : "Add knock"}</button>
        <button onClick={onCancel} className="rounded-md px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
      </div>
    </div>
  );
}

// ── One person's day ─────────────────────────────────────────────────────────

function PersonRow({ person, row, knocks, actual, weekly, day, canWrite, myId, open, onToggle, onSaved, areas, onAreasChanged, stops, people }: {
  person: Person; row: DailyCommitment | undefined; knocks: TeamKnock[]; actual: DayActuals | undefined;
  weekly: WeeklyGoalsMap[string] | undefined; day: string; canWrite: boolean; myId: string | null;
  open: boolean; onToggle: () => void; onSaved: () => void;
  areas: AreaStats[] | null; onAreasChanged: () => void;
  stops: DayStop[] | null; people: Person[];
}) {
  const [areasOpen, setAreasOpen] = useState(false);
  const myAreas = useMemo(
    () => sortAreasForDay((areas ?? []).filter((a) => a.reps.some((r) => r.user_id === person.id)), day),
    [areas, person.id, day],
  );
  const knock = knocks.find((k) => k.id === row?.team_knock_id);
  const status = !row
    ? <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Not yet</span>
    : row.is_off
    ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">Off{row.off_reason ? ` · ${row.off_reason}` : ""}</span>
    : <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${row.confirmed_late ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
        ✓ {timeShort(row.confirmed_at)}{row.confirmed_late ? " · late" : ""}
      </span>;

  return (
    <li>
      <div className="flex items-stretch">
      <button type="button" onClick={canWrite ? onToggle : undefined}
        className={`flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1.5 py-2.5 pl-3 pr-2 text-left ${canWrite ? "hover:bg-slate-50" : "cursor-default"}`}>
        <div className="min-w-[150px] flex-1">
          <div className="text-sm font-semibold text-slate-900">{person.full_name} <span className="text-[10px] font-bold text-slate-400">{ROLE_SHORT[person.role] ?? ""}</span></div>
          {row?.area && !row.is_off && <div className="text-[11px] text-slate-500">📍 {row.area}</div>}
        </div>
        {status}
        {row && !row.is_off && (
          <>
            <span className="flex gap-0.5">
              {BLOCKS.map((b) => (
                <span key={b} className={`w-8 rounded text-center text-[10px] font-bold ${row.blocks[b] ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-400"}`}>{BLOCK_LABEL[b]}</span>
              ))}
            </span>
            {row.knock_status === "in" && knock && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-700">Knock {knock.label}</span>}
            {row.knock_status === "out" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">Out of knock{row.knock_out_reason ? ` · ${row.knock_out_reason}` : ""}</span>}
            <span className="text-[11px] tabular-nums text-slate-600">
              <GoalDone label="Apps" goal={row.goal_apps} done={actual?.apps} />{" · "}
              <GoalDone label="Claims" goal={row.goal_claims} done={actual?.claims} />{" · "}
              <GoalDone label="IA" goal={row.goal_ia} done={actual?.ia} />
            </span>
          </>
        )}
        {canWrite && <span className="ml-auto text-xs font-bold text-orange-600">{open ? "Close" : row ? "Edit" : "Confirm"}</span>}
      </button>
      {/* Assigned canvas areas — its own toggle (a button can't sit inside the row button). */}
      <button type="button" onClick={() => setAreasOpen((v) => !v)} aria-expanded={areasOpen}
        title={myAreas.length ? myAreas.map(areaName).join(" · ") : "No assigned canvas area"}
        className={`mr-2 min-w-[6.5rem] flex-none self-center whitespace-nowrap rounded-full px-2.5 py-1 text-center text-[11px] font-bold ${
          areasOpen ? "bg-sky-600 text-white"
            : myAreas.length ? "bg-sky-50 text-sky-700 hover:bg-sky-100"
            : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
        📐 {areas === null ? "…" : myAreas.length === 0 ? "No area" : `${myAreas.length} ${myAreas.length === 1 ? "area" : "areas"}`} {areasOpen ? "▴" : "▾"}
      </button>
      </div>
      {/* Where they'll be: the day's build / adjuster meeting + Area here. A rep
          reading the board only gets their own deals from canvas_day_stops, so
          only their own row shows the line — a teammate's row would wrongly
          read "no stop". */}
      {!row?.is_off && (canWrite || person.id === myId) && (
        <PersonStops person={person} stops={stops} areas={areas} day={day} canWrite={canWrite} people={people} onChanged={onAreasChanged} />
      )}
      {areasOpen && (
        areas === null
          ? <div className="border-t border-sky-100 bg-sky-50/40 px-3 py-3 text-xs text-slate-500">Loading areas…</div>
          : <PersonAreasPanel person={person} areas={areas} canWrite={canWrite} onChanged={onAreasChanged} />
      )}
      {open && canWrite && (
        <RowEditor person={person} row={row} knocks={knocks} weekly={weekly} day={day} myId={myId} onSaved={onSaved}
          assignedAreas={myAreas} />
      )}
    </li>
  );
}

function GoalDone({ label, goal, done }: { label: string; goal: number | null; done: number | undefined }) {
  const hit = goal != null && goal > 0 && (done ?? 0) >= goal;
  return <span className={hit ? "font-bold text-emerald-700" : ""}>{label} {fmt(done ?? 0)}/{fmt(goal)}</span>;
}

function RowEditor({ person, row, knocks, weekly, day, myId, onSaved, assignedAreas }: {
  person: Person; row: DailyCommitment | undefined; knocks: TeamKnock[]; weekly: WeeklyGoalsMap[string] | undefined;
  day: string; myId: string | null; onSaved: () => void; assignedAreas: AreaStats[];
}) {
  const pre = prefillGoals(weekly, day);
  const [blocks, setBlocks] = useState<Blocks>(row?.blocks ?? { am: false, pm: true, eve: true });
  const [area, setArea] = useState(row?.area ?? "");
  const [knockSel, setKnockSel] = useState<string>(
    row?.knock_status === "out" ? "out" : row?.team_knock_id ?? (knocks.length === 1 ? knocks[0].id : ""));
  const [outReason, setOutReason] = useState(row?.knock_out_reason ?? "");
  const [apps, setApps] = useState(String(row?.goal_apps ?? pre.apps));
  const [claims, setClaims] = useState(String(row?.goal_claims ?? pre.claims));
  const [ia, setIa] = useState(String(row?.goal_ia ?? pre.ia));
  const [note, setNote] = useState(row?.note ?? "");
  const [offMode, setOffMode] = useState(false);
  const [offReason, setOffReason] = useState(row?.off_reason ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const num = (s: string) => { const n = Number(s); return Number.isFinite(n) && n >= 0 ? Math.round(n * 2) / 2 : 0; };

  async function confirm() {
    if (!BLOCKS.some((b) => blocks[b])) { setErr("Pick at least one block they're working, or mark them off."); return; }
    if (knocks.length && !knockSel) { setErr("Pick their knock, or mark them out of it."); return; }
    if (knockSel === "out" && outReason.trim().length < 2) { setErr("Add why they're out of the knock."); return; }
    setSaving(true); setErr(null);
    try {
      await saveCommitment({
        user_id: person.id, day, is_off: false, off_reason: null, blocks, area: area.trim() || null,
        team_knock_id: knockSel && knockSel !== "out" ? knockSel : null,
        knock_status: knockSel === "out" ? "out" : knockSel ? "in" : null,
        knock_out_reason: knockSel === "out" ? outReason.trim() : null,
        goal_apps: num(apps), goal_claims: num(claims), goal_ia: num(ia), note: note.trim() || null,
        confirmed_by: row?.confirmed_by ?? myId,
      });
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  }

  async function markOff() {
    if (offReason.trim().length < 2) { setErr("Add a reason (PTO, sick, …)."); return; }
    setSaving(true); setErr(null);
    try {
      await saveCommitment({
        user_id: person.id, day, is_off: true, off_reason: offReason.trim(), blocks: { am: false, pm: false, eve: false },
        area: null, team_knock_id: null, knock_status: null, knock_out_reason: null,
        goal_apps: null, goal_claims: null, goal_ia: null, note: note.trim() || null,
        confirmed_by: row?.confirmed_by ?? myId,
      });
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  }

  async function clear() {
    if (!row) return;
    setSaving(true);
    try { await deleteCommitment(row.id); onSaved(); } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  }

  const input = "w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm";
  return (
    <div className="space-y-3 border-t border-orange-200 bg-orange-50/40 px-3 py-3">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <div className="mb-1 text-[11px] font-semibold text-slate-500">Working</div>
          <div className="flex gap-1">
            {BLOCKS.map((b) => (
              <button key={b} type="button" aria-pressed={blocks[b]} onClick={() => setBlocks({ ...blocks, [b]: !blocks[b] })}
                className={`w-12 rounded-md py-1.5 text-xs font-bold ${blocks[b] ? "bg-orange-500 text-white" : "border border-slate-300 bg-white text-slate-500"}`}>
                {BLOCK_LABEL[b]}
              </button>
            ))}
          </div>
        </div>
        <label className="min-w-[180px] flex-1 text-[11px] font-semibold text-slate-500">Area
          <input id={`dc-area-${person.id}`} className={input} value={area} onChange={(e) => setArea(e.target.value)} placeholder="Where they're working" />
          {assignedAreas.length > 0 && (
            <span className="mt-1 flex flex-wrap items-center gap-1 font-normal">
              <span className="text-[10px] text-slate-400">Assigned:</span>
              {assignedAreas.map((a) => (
                <button key={a.area_group_id} type="button" onClick={() => setArea(areaName(a))}
                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${area === areaName(a) ? "bg-sky-600 text-white" : "bg-sky-50 text-sky-700 hover:bg-sky-100"}`}>
                  📐 {areaName(a)}
                </button>
              ))}
            </span>
          )}
        </label>
        {knocks.length > 0 && (
          <label className="min-w-[180px] text-[11px] font-semibold text-slate-500">Team knock
            <select id={`dc-knock-${person.id}`} className={input} value={knockSel} onChange={(e) => setKnockSel(e.target.value)}>
              <option value="">— pick —</option>
              {knocks.map((k) => <option key={k.id} value={k.id}>In · Knock {k.label} ({fmtClock(k.starts_at)})</option>)}
              <option value="out">Out of the knock</option>
            </select>
          </label>
        )}
      </div>
      {knockSel === "out" && (
        <label className="block text-[11px] font-semibold text-slate-500">Why they're out
          <input id={`dc-out-${person.id}`} className={input} value={outReason} onChange={(e) => setOutReason(e.target.value)} placeholder="Adjuster meeting at 5" />
        </label>
      )}
      <div className="flex flex-wrap items-end gap-3">
        {([["Apps", apps, setApps, pre.apps], ["Claims", claims, setClaims, pre.claims], ["Signed IA", ia, setIa, pre.ia]] as const).map(([lbl, v, set, def]) => (
          <label key={lbl} className="w-24 text-[11px] font-semibold text-slate-500">{lbl}
            <input id={`dc-${lbl}-${person.id}`} type="number" step="0.5" min="0" inputMode="decimal" className={input} value={v} onChange={(e) => set(e.target.value)} />
            <span className="text-[10px] font-normal text-slate-400">default {fmt(def)}</span>
          </label>
        ))}
        <label className="min-w-[200px] flex-1 text-[11px] font-semibold text-slate-500">Note
          <input id={`dc-note-${person.id}`} className={input} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" />
        </label>
      </div>
      {err && <div className="text-xs text-rose-600">{err}</div>}
      {offMode ? (
        <div className="flex flex-wrap items-end gap-2">
          <label className="min-w-[200px] flex-1 text-[11px] font-semibold text-slate-500">Reason they're off
            <input id={`dc-off-${person.id}`} className={input} value={offReason} onChange={(e) => setOffReason(e.target.value)} placeholder="PTO, sick, family…" />
          </label>
          <button onClick={markOff} disabled={saving} className="rounded-md bg-slate-700 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">Mark off today</button>
          <button onClick={() => setOffMode(false)} className="rounded-md px-3 py-1.5 text-xs font-semibold text-slate-600">Back</button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button onClick={confirm} disabled={saving} className="rounded-md bg-orange-500 px-4 py-1.5 text-xs font-bold text-white disabled:opacity-50">
            {saving ? "Saving…" : row && !row.is_off ? "Save changes" : "Confirm day ✓"}
          </button>
          <button onClick={() => setOffMode(true)} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600">Off today…</button>
          {row && <button onClick={clear} disabled={saving} className="ml-auto rounded-md px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50">Clear</button>}
        </div>
      )}
    </div>
  );
}
